import * as React from "react";
import { useState, useEffect } from "react";
import { LocalizedLink } from "@/components/LocalizedLink";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { FeatureCardSimple } from "@/components/ui/feature-card-simple";
import { Badge } from "@/components/ui/badge";
import { ArrowUp, Thermometer, Droplets, Wind, Shield, Sparkles, Zap, Users, Radio, Footprints, Settings, Waves } from "lucide-react";
import { ProminentCTA } from "@/components/ui/prominent-cta";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { SEO } from "@/components/SEO";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { useOptimizedObserver } from "@/hooks/useOptimizedObserver";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { RelatedProducts } from "@/components/RelatedProducts";
import { Footer } from "@/components/Footer";
import { ContactForm } from "@/components/ui/contact-form";
import SocialShare from "@/components/blog/SocialShare";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { InstallationGuidePDF } from "@/components/InstallationGuidePDF";

// M3 Product Images
import m3HeroMain from "@/assets/m3-hero-main.jpg";
import m3MultipleFunctions from "@/assets/m3-multiple-functions.jpg";
import m3WaterPressure from "@/assets/m3-water-pressure.jpg";
import m3RemovablePanel from "@/assets/m3-removable-panel.jpg";
import m3RadarSensor from "@/assets/m3-radar-sensor.jpg";
import m3SmartCleaning from "@/assets/m3-smart-cleaning.jpg";
import m3SeatHeating from "@/assets/m3-seat-heating.jpg";
import m3WarmAirDrying from "@/assets/m3-warm-air-drying.jpg";
import m3Ergonomics from "@/assets/m3-ergonomics.jpg";
import m3Deodorization from "@/assets/m3-deodorization.jpg";
import m3MemoryButtons from "@/assets/m3-memory-buttons.jpg";
import m3WaterSaving from "@/assets/m3-water-saving.jpg";
import m3RemoteControl from "@/assets/m3-remote-control.jpg";
import m3SecurityFeatures from "@/assets/m3-security-features.jpg";
import m3Specifications from "@/assets/m3-specifications.jpg";
import m3PackageContents from "@/assets/m3-package-contents.jpg";
import m3FootKick from "@/assets/m3-foot-kick.jpg";

import { PriceFrom } from "@/components/products/PriceFrom";
export default function M3() {
  const [showBackToTop, setShowBackToTop] = useState(false);
  useScrollToTop();
  useOptimizedObserver();

  useEffect(() => {
    let rafId: number | null = null;
    
    const handleScroll = () => {
      if (rafId) return;
      
      rafId = requestAnimationFrame(() => {
        setShowBackToTop(window.scrollY > 400);
        rafId = null;
      });
    };
    
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productImages = [
    { src: m3HeroMain, alt: "M3 Smart Toilet with elegant elongated design in modern bathroom setting", title: "M3 Smart Toilet" },
    { src: m3MultipleFunctions, alt: "M3 Smart Toilet multiple functions including intelligent flushing and smart cleaning", title: "Multiple Functions" },
    { src: m3RadarSensor, alt: "M3 Smart Toilet radar sensor technology for hands-free operation", title: "Radar Sensor" },
    { src: m3SeatHeating, alt: "M3 Smart Toilet thermostatic seat heating with temperature display", title: "Seat Heating" },
    { src: m3Deodorization, alt: "M3 Smart Toilet deodorization system for fresh bathroom environment", title: "Deodorization" },
    { src: m3MemoryButtons, alt: "M3 Smart Toilet memory buttons for personalized user settings", title: "Memory Buttons" },
    { src: m3WaterSaving, alt: "M3 Smart Toilet water saving dual flush technology", title: "Water Saving" },
    { src: m3RemoteControl, alt: "M3 Smart Toilet remote control and voice control operation", title: "Remote Control" },
    { src: m3SecurityFeatures, alt: "M3 Smart Toilet safety and security features", title: "Security Features" },
    { src: m3Specifications, alt: "M3 Smart Toilet technical specifications and dimensions", title: "Specifications" },
    { src: m3PackageContents, alt: "M3 Smart Toilet package contents and accessories", title: "Package Contents" },
    { src: m3FootKick, alt: "M3 Smart Toilet foot kick sensor for hands-free seat control", title: "Foot Kick Sensor" },
  ];

  const premiumFeatures = [
    {
      icon: <Zap className="w-8 h-8 text-amber-500" />,
      title: "No Water Pressure Limit",
      description: "Built-in tank system works in high-rise buildings and older homes with low water pressure"
    },
    {
      icon: <Droplets className="w-8 h-8 text-blue-400" />,
      title: "Smart Cleaning System",
      description: "Hip washing, female washing, and self-cleaning nozzle for complete spa-like hygiene experience"
    },
    {
      icon: <Thermometer className="w-8 h-8 text-orange-400" />,
      title: "Thermostatic Seat Heating",
      description: "Warm touch with thermostatic cushion for comfortable sitting at your preferred temperature"
    },
    {
      icon: <Wind className="w-8 h-8 text-cyan-400" />,
      title: "Warm Air Drying",
      description: "Multi-level warm air with efficient drying speed - combined with water washing for complete comfort"
    },
    {
      icon: <Radio className="w-8 h-8 text-purple-400" />,
      title: "Radar Sensor Technology",
      description: "Automatic lid and seat cover opening when you approach - foot kick option for seat ring control"
    },
    {
      icon: <Settings className="w-8 h-8 text-slate-400" />,
      title: "Removable Panel Design",
      description: "Access built-in foam bottle, power-off system, and instant heating display with dustproof protection"
    }
  ];

  const keyFeatures = [
    "Controller control with voice support",
    "No water pressure limit technology",
    "Smart cleaning bidet system",
    "Thermostatic seat heating at 36°C",
    "Radar sensor auto open/close lid",
    "Foot kick to open seat ring",
    "Multi-level warm air drying",
    "Intelligent flushing with display",
    "Slow close lid mechanism",
    "Hip, female, and self-wash modes",
    "Wider U-shaped ergonomic seat",
    "Removable dustproof panel",
    "Built-in foam bottle system",
    "One-piece elongated design",
    "Dual flush water saving"
  ];

  const specifications = [
    { label: "Design", value: "One-Piece Elongated" },
    { label: "Dimensions", value: "680mm × 380mm × 520mm" },
    { label: "Heating Method", value: "Instant heating type" },
    { label: "Drainage", value: "Ground drainage" },
    { label: "Roughing-in", value: "S-trap 300/400mm" },
    { label: "Rated Voltage", value: "110V/220V~" },
    { label: "Seat Heating", value: "Thermostatic 36°C display" },
    { label: "Wash Modes", value: "Hip, Female, Self-cleaning" },
    { label: "Air Dryer", value: "Multi-level warm air" },
    { label: "Auto Functions", value: "Open/Close lid, Flush" },
    { label: "Control", value: "Controller + Voice Control" },
    { label: "Sensor", value: "Radar proximity sensing" },
    { label: "Flush Type", value: "Intelligent dual flush" },
    { label: "Lid Closing", value: "Slow down soft close" },
    { label: "Special Features", value: "Removable panel, foam bottle" },
    { label: "Ergonomics", value: "Wider U-shaped seat" },
    { label: "Water Pressure", value: "No limit - built-in tank" },
    { label: "Waterproof Rating", value: "IPX4" },
    { label: "Warranty", value: "3 Years Comprehensive" },
    { label: "Installation", value: "Professional Required" },
    { label: "Flip Options", value: "Manual or Automatic" }
  ];

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "M3 Smart Toilet with Bidet",
    "description": "M3 Smart Toilet featuring controller and voice control, seat heating, 4-second quiet flush, adjustable ambient lighting, auto open/close lid, dual memory, built-in tank, foot sensor, heated seat, and one-piece elongated design.",
    "brand": {
      "@type": "Brand",
      "name": "Aureum VII Trading"
    },
    "image": [
      "https://www.aureum7trading.com/images/products/m3-hero-main.jpg"
    ],
    "gtin13": "0795479810015",
    "manufacturer": {
      "@type": "Organization",
      "name": "Aureum VII Trading"
    },
    "sku": "M3-SMARTTOILET",
    "mpn": "M3",
    "offers": {
      "@type": "Offer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "price": 600,
      "priceValidUntil": "2026-12-31",
      "url": "https://www.aureum7trading.com/products/m3",
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "Aureum VII Trading",
        "url": "https://www.aureum7trading.com"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "EUR"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 3,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 10,
            "maxValue": 40,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "@id": "https://www.aureum7trading.com/return-policy",
        "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn",
        "itemCondition": "https://schema.org/NewCondition",
        "refundType": "https://schema.org/FullRefund"
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 text-foreground">
      <SEO
        title="M3 Smart Toilet | Voice Control & Radar Sensor Lid"
        description="M3 smart toilet with voice control, radar-sensor auto open/close, heated seat, warm-air drying and a bidet that works with no water pressure limit."
        keywords="M3 smart toilet, voice control toilet, radar sensor toilet, auto open close toilet, bidet toilet, smart bathroom, European smart toilet, luxury bathroom 2025, heated seat toilet"
        url="/products/m3"
        image="https://www.aureum7trading.com/images/products/m3-hero-main.jpg"
        structuredData={productStructuredData}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "M3 Smart Toilet", url: "/products/m3" }
        ]}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative pt-20 pb-0 overflow-hidden">
        <div className="relative w-full h-[70vh] md:h-[80vh]">
          <img 
            src={m3HeroMain} 
            alt="M3 Smart Toilet with elegant one-piece elongated design featuring controller control, seat heating, and voice control"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12">
            <div className="container mx-auto max-w-7xl">
              <Badge variant="outline" className="border-amber-300/50 text-white bg-amber-500/20 backdrop-blur-sm mb-4 font-medium">
                Voice Control & Radar Sensor
              </Badge>
              <h1 className="font-playfair text-5xl md:text-7xl font-light mb-4 text-white tracking-tight">
                M3 Smart Toilet
                <span className="block text-2xl md:text-3xl text-amber-200 font-medium mt-2">
                  Intelligent Living • Complete Comfort
                </span>
              </h1>
              <PriceFrom price={600} />
              <p className="font-inter text-xl text-white/90 max-w-2xl mb-6">
                Experience hands-free luxury with radar sensor technology, voice control, and thermostatic seat heating.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
                  <a href="#features">Explore Features</a>
                </Button>
                <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10" asChild>
                  <LocalizedLink to="/contact">Request Quote</LocalizedLink>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Multiple Functions Overview */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer" id="features">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-4xl md:text-5xl font-light mb-6 text-primary">
              Multiple Functions<br />Complete Experience
            </h2>
            <p className="font-inter text-xl text-muted-foreground mb-8">
              The M3 combines intelligent flushing, smart cleaning, seat heating, radar sensing, and slow-down cover technology for a complete bathroom transformation.
            </p>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center p-4 bg-white rounded-xl shadow-soft">
                <Waves className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                <p className="font-medium text-sm">Intelligent Flush</p>
                <p className="text-xs text-muted-foreground">4-second quiet flush</p>
              </div>
              <div className="text-center p-4 bg-white rounded-xl shadow-soft">
                <Radio className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="font-medium text-sm">Radar Sensor</p>
                <p className="text-xs text-muted-foreground">Auto lid control</p>
              </div>
            </div>
          </div>
          <div>
            <ImageLightbox
              src={m3MultipleFunctions}
              alt="M3 Smart Toilet multiple functions including no water pressure limit, smart cleaning, seat heating, intelligent flushing, radar sensor, and slow down cover"
              className="w-full h-auto rounded-2xl shadow-luxury"
            />
          </div>
        </div>
      </section>

      {/* No Water Pressure Limit */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-blue-50 to-cyan-50/50">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ImageLightbox
                src={m3WaterPressure}
                alt="M3 Smart Toilet no water pressure limit technology showing transparent water tank system suitable for high buildings and old houses"
                className="w-full h-auto rounded-2xl shadow-luxury"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-10 h-10 text-amber-500" />
                <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">Universal Compatibility</Badge>
              </div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                No Water Pressure<br />Limit Technology
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Breaking through the water pressure limitation - high buildings and old houses can also be used. The built-in tank system ensures consistent performance regardless of your home's water pressure.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-muted-foreground">Works in high-rise buildings</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-muted-foreground">Perfect for older homes</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="text-muted-foreground">Consistent performance always</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Radar Sensor Section */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Radio className="w-10 h-10 text-purple-500" />
              <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">Hands-Free Technology</Badge>
            </div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Radar Sensor<br />Opens The Cover
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              When you get closer, the lid and seat cover automatically turns up. You can also extend your foot to activate - the system detects your presence and responds intelligently.
            </p>
            
            <div className="space-y-6 mt-8">
              <div className="bg-amber-50 rounded-xl p-4">
                <h4 className="font-medium text-primary mb-3 flex items-center gap-2">
                  <Footprints className="w-5 h-5 text-amber-500" />
                  For Men
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <span>• Sensor-activated seat cover</span>
                  <span>• Kick to open toilet seat</span>
                  <span>• Use toilet (stand)</span>
                  <span>• Kick/Auto flush + close lid</span>
                </div>
              </div>
              <div className="bg-rose-50 rounded-xl p-4">
                <h4 className="font-medium text-primary mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5 text-rose-500" />
                  For Ladies
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <span>• Body-opening seat cover</span>
                  <span>• Use the toilet (sit)</span>
                  <span>• Auto flush + close lid</span>
                  <span>• Switch to kick-open option</span>
                </div>
              </div>
            </div>
          </div>
          <div>
            <ImageLightbox
              src={m3RadarSensor}
              alt="M3 Smart Toilet radar sensor automatic lid opening demonstration showing foot kick activation and proximity sensing for men and women"
              className="w-full h-auto rounded-2xl shadow-luxury"
            />
          </div>
        </div>
      </section>

      {/* Smart Cleaning Section */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ImageLightbox
                src={m3SmartCleaning}
                alt="M3 Smart Toilet smart cleaning bidet system showing hip washing, female washing, and self-cleaning nozzle modes"
                className="w-full h-auto rounded-2xl shadow-luxury"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Droplets className="w-10 h-10 text-blue-500" />
                <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white">Spa Experience</Badge>
              </div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Smart Cleaning<br />Various Systems
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Let the hips enjoy the comfortable spa experience with various cleaning systems designed for complete hygiene and comfort.
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
                  <Waves className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">Hip Washing</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl">
                  <Droplets className="w-6 h-6 text-rose-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">Female Washing</p>
                </div>
                <div className="text-center p-4 bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl">
                  <Sparkles className="w-6 h-6 text-cyan-500 mx-auto mb-2" />
                  <p className="font-medium text-sm">Self Washing</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Seat Heating Section */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Thermometer className="w-10 h-10 text-orange-500" />
              <Badge className="bg-gradient-to-r from-orange-500 to-amber-500 text-white">36°C Comfort</Badge>
            </div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Seat Heating<br />Warm Touching
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Thermostatic cushion provides comfortable touching with warm seat technology. The LED display shows real-time temperature at 36°C for perfect comfort in any season.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-muted-foreground">Thermostatic temperature control</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-muted-foreground">LED display shows temperature</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                <span className="text-muted-foreground">Comfortable cushion design</span>
              </li>
            </ul>
          </div>
          <div>
            <ImageLightbox
              src={m3SeatHeating}
              alt="M3 Smart Toilet thermostatic seat heating with 36°C display showing warm heating elements throughout the seat"
              className="w-full h-auto rounded-2xl shadow-luxury"
            />
          </div>
        </div>
      </section>

      {/* Warm Air Drying Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-amber-50 to-orange-50/50">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ImageLightbox
                src={m3WarmAirDrying}
                alt="M3 Smart Toilet warm air drying system with multi-level temperature settings for efficient drying"
                className="w-full h-auto rounded-2xl shadow-luxury"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Wind className="w-10 h-10 text-cyan-500" />
                <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white">Efficient Drying</Badge>
              </div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Warm Air Drying<br />Multi-Level Comfort
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Multi-level warm air button combined with water washing has an efficient drying speed. Experience complete comfort without the need for toilet paper.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                  <span className="text-muted-foreground">Multi-level temperature control</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                  <span className="text-muted-foreground">Efficient drying speed</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                  <span className="text-muted-foreground">Combined with water washing</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Ergonomics Section */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Ergonomics<br />Comfortable Position
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Wider U-shaped seat with large seating area provides a comfortable and natural solution. The ergonomic design promotes healthy posture and reduces strain.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Comfortable sitting position</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Wider U-shaped seat design</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Large seating area</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <span className="text-muted-foreground">Natural constipation relief</span>
              </li>
            </ul>
          </div>
          <div>
            <ImageLightbox
              src={m3Ergonomics}
              alt="M3 Smart Toilet ergonomic design with wider U-shaped seat showing comfortable sitting position"
              className="w-full h-auto rounded-2xl shadow-luxury"
            />
          </div>
        </div>
      </section>

      {/* Removable Panel Section */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ImageLightbox
                src={m3RemovablePanel}
                alt="M3 Smart Toilet removable panel revealing built-in foam bottle, power-off system, and instant heating display"
                className="w-full h-auto rounded-2xl shadow-luxury"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Settings className="w-10 h-10 text-slate-500" />
                <Badge className="bg-gradient-to-r from-slate-500 to-gray-500 text-white">Easy Access</Badge>
              </div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Removable Panel<br />Convenient Design
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Removable panel hiding the built-in foam bottle, power-off system, instant heating system display - effective dustproof and waterproof protection.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-slate-500 rounded-full"></div>
                  <span className="text-muted-foreground">Built-in foam bottle system</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-slate-500 rounded-full"></div>
                  <span className="text-muted-foreground">Power-off system access</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-slate-500 rounded-full"></div>
                  <span className="text-muted-foreground">Dustproof & waterproof</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Deodorization Section */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-10 h-10 text-green-500" />
              <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white">Fresh Air</Badge>
            </div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Deodorization<br />Fresh Environment
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Advanced deodorization system eliminates odors at the source, keeping your bathroom fresh and pleasant at all times.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-muted-foreground">Automatic odor elimination</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-muted-foreground">Silent operation</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-muted-foreground">Energy efficient</span>
              </li>
            </ul>
          </div>
          <div>
            <ImageLightbox
              src={m3Deodorization}
              alt="M3 Smart Toilet deodorization system for fresh bathroom environment"
              className="w-full h-auto rounded-2xl shadow-luxury"
            />
          </div>
        </div>
      </section>

      {/* Memory Buttons & Remote Control Section */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-indigo-50 to-violet-50/50">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ImageLightbox
                src={m3MemoryButtons}
                alt="M3 Smart Toilet memory buttons for personalized user settings"
                className="w-full h-auto rounded-2xl shadow-luxury"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Users className="w-10 h-10 text-indigo-500" />
                <Badge className="bg-gradient-to-r from-indigo-500 to-violet-500 text-white">Personalized</Badge>
              </div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Memory Buttons<br />Personal Preferences
              </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Store your preferred settings with memory buttons. Each family member can enjoy their customized comfort experience.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span className="text-muted-foreground">2 user memory profiles</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span className="text-muted-foreground">One-touch preference recall</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                  <span className="text-muted-foreground">Water temperature & pressure saved</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Water Saving Section */}
      <section className="py-16 px-6 lg:px-12 container mx-auto max-w-7xl fade-in-observer">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Droplets className="w-10 h-10 text-teal-500" />
              <Badge className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white">Eco-Friendly</Badge>
            </div>
            <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
              Water Saving<br />Dual Flush System
            </h2>
            <p className="font-inter text-lg text-muted-foreground mb-6">
              Intelligent dual flush system uses only 4L or 6L per flush, saving thousands of liters annually while maintaining powerful performance.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                <span className="text-muted-foreground">4L light flush option</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                <span className="text-muted-foreground">6L full flush option</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
                <span className="text-muted-foreground">Up to 50% water savings</span>
              </li>
            </ul>
          </div>
          <div>
            <ImageLightbox
              src={m3WaterSaving}
              alt="M3 Smart Toilet water saving dual flush technology"
              className="w-full h-auto rounded-2xl shadow-luxury"
            />
          </div>
        </div>
      </section>

      {/* Security Features Section */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <ImageLightbox
                src={m3SecurityFeatures}
                alt="M3 Smart Toilet safety and security features"
                className="w-full h-auto rounded-2xl shadow-luxury"
              />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Shield className="w-10 h-10 text-red-500" />
                <Badge className="bg-gradient-to-r from-red-500 to-rose-500 text-white">Safety First</Badge>
              </div>
              <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
                Security Features<br />Complete Protection
            </h2>
              <p className="font-inter text-lg text-muted-foreground mb-6">
                Multiple safety systems protect you and your family. IPX4 waterproofing, child lock, and automatic power-off ensure peace of mind.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-muted-foreground">IPX4 waterproof rating</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-muted-foreground">Child safety lock</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-muted-foreground">Auto power-off protection</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Installation Guide Download */}
      <section className="py-12 px-6 lg:px-12 bg-gradient-to-br from-amber-50 to-orange-50/50">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-4">
            Installation Guide
          </h2>
          <p className="font-inter text-lg text-muted-foreground mb-6">
            Download the comprehensive M3 installation guide with step-by-step instructions
          </p>
          <InstallationGuidePDF product="M3" className="mx-auto" />
        </div>
      </section>

      {/* Premium Features Grid */}
      <section className="py-20 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <div className="text-center mb-16">
            <h2 className="font-playfair text-4xl md:text-5xl font-light mb-6 text-primary">
              Premium Smart Features
            </h2>
            <p className="font-inter text-xl text-muted-foreground max-w-2xl mx-auto">
              Every feature designed for comfort, hygiene, and intelligent living
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {premiumFeatures.map((feature, index) => (
              <FeatureCardSimple
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                className="hover:scale-105 transition-transform duration-300"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 px-6 lg:px-12 bg-gradient-to-br from-stone-50 to-amber-50/30">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">
            Product Gallery
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {productImages.map((image, index) => (
              <ImageLightbox
                key={index}
                src={image.src}
                alt={image.alt}
                title={image.title}
                className="aspect-square object-cover rounded-xl"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-16 px-6 lg:px-12 bg-white/70 backdrop-blur-sm">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">
            Technical Specifications
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {specifications.map((spec, index) => (
              <div key={index} className="bg-white rounded-lg p-4 shadow-soft flex justify-between items-center">
                <span className="font-medium text-muted-foreground">{spec.label}</span>
                <span className="text-primary">{spec.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Features List */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <h2 className="font-playfair text-3xl font-medium text-primary mb-8 text-center">
            Complete Feature List
          </h2>
          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {keyFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-soft">
                <Shield className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shipping Info */}
      <section className="py-12 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <ShippingTimeInfo />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-12 bg-gradient-to-br from-primary to-primary/80">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-4xl md:text-5xl font-light mb-6 text-white">
            Ready to Upgrade?
          </h2>
          <p className="font-inter text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Transform your bathroom with the M3 Smart Toilet. Contact us for pricing and installation options.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-primary hover:bg-white/90" asChild>
              <LocalizedLink to="/contact">Request Quote</LocalizedLink>
            </Button>
            <Button variant="outline" size="lg" className="border-white/50 text-white hover:bg-white/10" asChild>
              <LocalizedLink to="/smart-toilets">View All Products</LocalizedLink>
            </Button>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <section className="py-16 px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <RelatedProducts currentProduct="M3" />
        </div>
      </section>

      <Footer />

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 right-6 z-40 w-12 h-12 bg-primary text-white rounded-full shadow-luxury flex items-center justify-center hover:bg-primary/90 transition-all duration-300"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
